<template>
    <div
        id="sidebar-feature-usage-container"
        v-show="singleFeatureUsage"
        class="'w-fit h-10 rounded-md text-sm flex items-center hover:bg-won-bg-blue p-0"
        :class="collapsed ? 'justify-center' : 'justify-between'"
    >
        <div v-if="!collapsed && singleFeatureUsage" class="w-full">
            <VMenu placement="right-end" class="min-w-full" :triggers="['hover', 'focus']">
                <div class="block text-gray-700 no-underline rounded transition">
                    <div
                        v-if="isSubscribe"
                        class="w-full flex justify-center items-center gap-1 p-3 bg-dark-green-gradient rounded cursor-pointer"
                    >
                        <span class="font-bold text-white text-base">
                            Premium User
                        </span>

                        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.22668 5.44L9.28002 8.5L6.22668 11.56L7.16668 12.5L11.1667 8.5L7.16668 4.5L6.22668 5.44Z" fill="#8897AA"/>
                        </svg>
                    </div>

                    <pdf-feature-usage-badge
                        v-else-if="singleFeatureUsage"
                        :isSubscribe="isSubscribe"
                        :isUnlimitedForPremium="singleFeatureUsage.isUnlimitedForPremium"
                        is-single-feature
                        :usage='singleFeatureUsage?.usage'
                        :description="features.find(item => item.name === singleFeatureUsage.name).description" 
                    />
                </div>

                <template #popper>
                    <div
                        class="border border-won-sand-light bg-white shadow-card w-max flex-col rounded gap-0.5"
                    >
                        <pdf-feature-usage-badge
                            v-for="item of features"
                            :isSubscribe="isSubscribe"
                            :key="item.id"
                            :usage='item.usage'
                            :description="item.description"
                            :isUnlimitedForPremium="item.isUnlimitedForPremium"
                        />
                    </div>
                </template>
            </VMenu>
        </div>
        
        <div class="w-full" v-else>
            <VMenu
                placement="right-end"
                class="min-w-full flex items-center justify-center gap-2 px-2"
                :triggers="['hover', 'focus']"
            >
                <div
                    v-if="singleFeatureUsage"
                    v-tooltip="{ content: `${singleFeatureUsage?.usage} ${singleFeatureUsage?.description} left`, delay: { show: collapsed ? 500 : 999999, hide: 100 } }"
                    class="
                        text-white min-w-6 h-6 flex cursor-pointer mx-auto items-center
                        justify-center rounded-full no-underline transition
                    "
                    :class="singleFeatureUsage?.usage != 0 || isSubscribe ? 'bg-won-green' : 'bg-red-500'"
                >
                    <span v-if="singleFeatureUsage.name !== 'auto_apply' && isSubscribe" class="text-[12px]">
                        ∞
                    </span>
                    <span class="text-[10px]" v-else>
                        {{ singleFeatureUsage.usage }}
                    </span>
                </div>

                <template #popper>
                    <div
                        class="border border-[#F4F4F4] bg-white shadow-card w-max flex-col rounded gap-[2px]"
                    >
                        <pdf-feature-usage-badge
                            v-for="item of features"
                            :isSubscribe="isSubscribe"
                            :key="item.id"
                            :usage='item.usage'
                            :description="item.description"
                            :isUnlimitedForPremium="item.isUnlimitedForPremium"
                        />
                    </div>
                </template>
            </VMenu>
        </div>
    </div>
</template>

<script setup>
import { watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from "vue-router";

import { useFeatureUsageStore } from '../../store/feature-usage.store';
import { useSubscriptionPlanStore } from "../../store/subscription.store";

import PdfFeatureUsageBadge from "./components/badge/pdf-feature-usage-badge";

import { FEATURES_DESCRIPTION } from "../../storage";

const props = defineProps({
    collapsed: {
        type: Boolean,
        default: false,
    }
})

const route = useRoute();
const featureUsageStore = useFeatureUsageStore();
const subscriptionStore = useSubscriptionPlanStore();
const { userFeatureUsage, singleFeatureUsage } = storeToRefs(featureUsageStore);

featureUsageStore.setActiveRouteFeature(route.meta.feature);
featureUsageStore.setSingleFeatureUsage(featureUsageStore.fetchFeatureUsage(route.meta.feature));

const { isSubscribe } = storeToRefs(subscriptionStore)

const features = computed(() => {
    return userFeatureUsage.value.map(({ name: featureName, usage }) => {
        let { action, description, premiumDescription, isUnlimitedForPremium } = FEATURES_DESCRIPTION[featureName]
        
        if(usage !== 1){
            action = `${action}s`
        }

        return {
            description: isSubscribe.value ? premiumDescription.replace('<<action>>', action) : description.replace('<<action>>', action),
            usage,
            name: featureName,
            isUnlimitedForPremium
        }
    })
})

watch(
  () => route.meta,
  (newMeta, oldMeta) => {
    if (newMeta.feature !== oldMeta.feature) { // Check if the feature has changed
      const { fetchFeatureUsage } = useFeatureUsageStore();
      featureUsageStore.setActiveRouteFeature(newMeta.feature);
      featureUsageStore.setSingleFeatureUsage(fetchFeatureUsage(newMeta.feature));
    }
  },
  { deep: true }
);
</script>
